import React, { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const Bullets = ({ d }) => {
  const rowsN = Math.ceil(d.items.length / d.columns) || 0;
  const grid = Array.from({ length: rowsN }, (_, r) => Array.from({ length: d.columns }, (_, c) => d.items[c * rowsN + r] || ''));
  return (
    <div className="bg-white border border-slate-300 rounded-md p-3" data-testid="bullet-preview-letter">
      <div className="font-serif text-sm mb-1">Repairs Detailed as Follows:</div>
      {d.items.length === 0 ? <div className="text-xs text-slate-400 italic">No bullets for this job.</div> : (
        <table className="w-full font-serif text-[12px]"><tbody>
          {grid.map((row, i) => <tr key={i}>{row.map((t, j) => <td key={j} className="align-top pr-2" style={{ width: `${100 / d.columns}%` }}>{t}</td>)}</tr>)}
        </tbody></table>
      )}
      <div className="text-[10px] text-slate-500 mt-2" data-testid="bullet-preview-layout">
        {d.bullets.length} bullets · {d.columns} column{d.columns > 1 ? 's' : ''} · {rowsN} line{rowsN === 1 ? '' : 's'} ·
        quote type "{d.quote_type || '—'}" · engine on this quote type: <b>{d.engine}</b>
      </div>
    </div>
  );
};

export const PreviewTab = ({ api }) => {
  const [ref, setRef] = useState('');
  const [qt, setQt] = useState('');
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!ref.trim()) return;
    setBusy(true);
    try { setD((await api.get(`/bullet-groups/preview/${encodeURIComponent(ref.trim())}`, { params: { quote_type: qt } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Not found'); setD(null); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="bullet-preview-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="bullet-preview-ref" value={ref} onChange={e => setRef(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()}
          placeholder="Appraisal no. e.g. A4868" className="border border-slate-200 rounded px-2 py-1 text-sm w-48" />
        <input data-testid="bullet-preview-qt" value={qt} onChange={e => setQt(e.target.value)} placeholder="Quote type (optional)"
          className="border border-slate-200 rounded px-2 py-1 text-sm w-64" />
        <button data-testid="bullet-preview-run" onClick={run} disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 rounded bg-[#316536] text-white text-sm hover:bg-[#25502a] transition-colors">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Preview
        </button>
      </div>
      <p className="text-[11px] text-slate-500">Read-only. Shows the new bullets exactly as they would print, even if the quote type is still on the old bullets.</p>
      {d && <Bullets d={d} />}
      {d && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="bullet-preview-lines">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-2 py-1.5">Line</th><th className="px-2 py-1.5">Bullet group</th><th className="px-2 py-1.5">Tokens</th><th className="px-2 py-1.5">Qty</th><th className="px-2 py-1.5">Linked to catalogue</th></tr>
            </thead>
            <tbody>
              {d.lines.map((l, i) => (
                <tr key={i} className={`border-t ${!l.bullet_group ? 'bg-rose-50' : !l.shown ? 'text-slate-400' : ''}`} data-testid={`bullet-preview-line-${i}`}>
                  <td className="px-2 py-1">{l.component_type} · {l.type_name}</td>
                  <td className="px-2 py-1">{l.bullet_group ? `${l.bullet_group} · ${l.bullet_title}${l.shown || (l.bullet_title || '').includes('hidden') ? '' : ' (hidden)'}` : 'NOT MAPPED — not printed'}</td>
                  <td className="px-2 py-1 font-mono text-[10px]">{Object.entries(l.bullet_tokens || {}).map(([k, v]) => `${k}=${v}`).join(', ')}</td>
                  <td className="px-2 py-1">{l.bullet_qty} {l.bullet_unit}</td>
                  <td className="px-2 py-1">{l.from_catalogue ? 'yes' : 'no (name match)'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
